// The site contract checklist on an asset's Contracts tab (spec 2026-10-11-17):
// decorates the contract-type groups of contract-groups.ts with the rows of
// GET /assets/{uid}/contract-checklist, adds the expected types nobody holds a
// contract for as empty groups, and orders the groups so what needs doing
// comes first.

import type { ContractChecklistContract, ContractChecklistRow } from '../api/schema'
import { contractTypeName, NO_CONTRACT_TYPE_GROUP, type ContractTypeGroupRows } from './contract-groups'

export type ChecklistStatus = ContractChecklistRow['status']

/** The compliance pill a checklist status borrows (STATUS_PILL_CONFIG keys), and its own label. */
export const CHECKLIST_STATUS: Record<ChecklistStatus, { pill: 'COMPLIANT' | 'EXPIRING' | 'NON_COMPLIANT' | 'NOT_APPLICABLE'; labelKey: string }> = {
  covered: { pill: 'COMPLIANT', labelKey: 'checklist.status.covered' },
  partial: { pill: 'EXPIRING', labelKey: 'checklist.status.partial' },
  missing: { pill: 'NON_COMPLIANT', labelKey: 'checklist.status.missing' },
  not_applicable: { pill: 'NOT_APPLICABLE', labelKey: 'checklist.status.not_applicable' },
}

/** A maintenance contract type: `WORK_MAINTENANCE` or a `WORK_MAINTENANCE_` subtype (contractexec.TypeCodesSQL). */
export function isMaintenance(code: string | undefined | null): boolean {
  return !!code && (code === 'WORK_MAINTENANCE' || code.startsWith('WORK_MAINTENANCE_'))
}

export type ContractDateItem =
  | { kind: 'lapsed' | 'terminate_before' | 'next_visit' | 'ends' | 'last_report'; date: string }
  | { kind: 'cadence_unknown' }

/**
 * The dates under a contract row, in display order, each only when its data
 * exists. A contract no longer in force and past its end shows only « Échu le »;
 * a tacit contract past its end is still in force, so it is not lapsed.
 * `today` is `YYYY-MM-DD`, compared as a string like the API's dates.
 */
export function contractDateItems(facts: ContractChecklistContract, typeCode: string | undefined, today: string): ContractDateItem[] {
  if (!facts.in_force && facts.end_date && facts.end_date < today) {
    return [{ kind: 'lapsed', date: facts.end_date }]
  }
  const items: ContractDateItem[] = []
  if (facts.termination_date && facts.termination_date >= today) items.push({ kind: 'terminate_before', date: facts.termination_date })
  if (facts.next_visit_on) items.push({ kind: 'next_visit', date: facts.next_visit_on })
  if (facts.end_date && facts.end_date >= today) items.push({ kind: 'ends', date: facts.end_date })
  if (facts.last_report_on) items.push({ kind: 'last_report', date: facts.last_report_on })
  if (facts.cadence_known === false && isMaintenance(typeCode)) items.push({ kind: 'cadence_unknown' })
  return items
}

interface ListedContract {
  uid: string
  name?: string | null
  code?: string | null
  contract_type?: { code?: string; names?: unknown } | null
}

/** One row of a group: a contract the tab lists, or one the checklist names that the tab does not. */
export interface ChecklistItem<C extends ListedContract> {
  uid: string
  /** The contract as the tab lists it; undefined for a contract held elsewhere. */
  contract?: C
  /** The checklist's facts on the contract, when the checklist names it. */
  facts?: ContractChecklistContract
}

export interface ChecklistGroup<C extends ListedContract> {
  typeCode: string
  contractType?: { code?: string; names?: unknown }
  row?: ContractChecklistRow
  items: ChecklistItem<C>[]
  rank: number
}

export interface ChecklistGroupsOptions {
  locale?: string
  /** `YYYY-MM-DD`, for the in-group order. */
  today: string
  /** A search, type or status filter is set: add no empty group and no held contract. */
  filtered?: boolean
  /** A column sort was clicked: keep the input order inside each group. */
  sorted?: boolean
}

/** The soonest of the next visit, the termination deadline and the end, ignoring past dates. */
function soonestDate(facts: ContractChecklistContract | undefined, today: string): string | undefined {
  if (!facts) return undefined
  const dates = [
    facts.next_visit_on,
    facts.termination_date && facts.termination_date >= today ? facts.termination_date : null,
    facts.end_date && facts.end_date >= today ? facts.end_date : null,
  ].filter((d): d is string => !!d)
  return dates.sort()[0]
}

function groupRank<C extends ListedContract>(g: ChecklistGroup<C>): number {
  if (g.typeCode === NO_CONTRACT_TYPE_GROUP) return 7
  const status = g.row?.status
  if (status === 'missing') return 0
  if (status === 'not_applicable') return 6
  if (g.items.some((i) => i.facts?.follow_up)) return 1
  if (status === 'partial') return 2
  if (g.items.some((i) => i.facts?.cadence_known === false && isMaintenance(i.facts.contract_type_code))) return 3
  if (status === 'covered') return 4
  return 5
}

/**
 * Attaches each checklist row to its contract-type group. Unless a filter is
 * set, adds an empty group for a row whose type has no listed contract, and,
 * to a row's group, each covering contract the tab does not list (held by the
 * site above, or on a descendant the tab hides). Groups are ordered by rank:
 * missing, a red contract, partial, unknown cadence, covered, not expected,
 * not applicable, `none` last; then by localized type name.
 */
export function checklistGroups<C extends ListedContract>(
  groups: ContractTypeGroupRows<C>[],
  rows: ContractChecklistRow[],
  factsByUid: Map<string, ContractChecklistContract>,
  options: ChecklistGroupsOptions,
): ChecklistGroup<C>[] {
  const { locale = 'en', today, filtered = false, sorted = false } = options
  const rowByCode = new Map(rows.map((r) => [r.contract_type.code, r]))
  const listed = new Set(groups.flatMap((g) => g.contracts.map((c) => c.uid)))

  const out = new Map<string, ChecklistGroup<C>>()
  for (const g of groups) {
    out.set(g.typeCode, {
      typeCode: g.typeCode,
      contractType: g.contractType,
      row: rowByCode.get(g.typeCode),
      items: g.contracts.map((c) => ({ uid: c.uid, contract: c, facts: factsByUid.get(c.uid) })),
      rank: 0,
    })
  }
  if (!filtered) {
    for (const row of rows) {
      const code = row.contract_type.code
      let group = out.get(code)
      if (!group) {
        group = { typeCode: code, contractType: row.contract_type, row, items: [], rank: 0 }
        out.set(code, group)
      }
      for (const uid of row.contract_uids) {
        if (listed.has(uid) || group.items.some((i) => i.uid === uid)) continue
        const facts = factsByUid.get(uid)
        if (facts) group.items.push({ uid, facts })
      }
    }
  }

  const name = (i: ChecklistItem<C>) => (i.contract?.name || i.contract?.code || i.facts?.name || '').toString()
  const result = [...out.values()]
  for (const g of result) {
    if (!sorted) {
      g.items.sort((a, b) => {
        const fa = a.facts?.follow_up ? 0 : 1
        const fb = b.facts?.follow_up ? 0 : 1
        if (fa !== fb) return fa - fb
        const da = soonestDate(a.facts, today)
        const db = soonestDate(b.facts, today)
        if (da !== db) {
          if (!da) return 1
          if (!db) return -1
          return da.localeCompare(db)
        }
        return name(a).localeCompare(name(b), locale)
      })
    }
    g.rank = groupRank(g)
  }
  const label = (g: ChecklistGroup<C>) => contractTypeName(g.contractType, locale) || g.typeCode
  return result.sort((a, b) => a.rank - b.rank || label(a).localeCompare(label(b), locale))
}
